import { Subject, type Observable } from 'rxjs';
import * as THREE from 'three/webgpu';
import type { Viewer } from '../../viewer/Viewer';
import { MeasurePairReferencePointResolver } from '../property/MeasurePairReferencePointResolver';
import { MeasureDxDyDzCalculator } from '../property/MeasureDxDyDzCalculator';
import { MeasurePropertyResolver } from '../property/MeasurePropertyResolver';
import { buildPairMeasurementCallouts } from './MeasurePairMeasurementCalloutBuilder';
import type {
    MeasureCalloutChangeEvent,
    MeasureCalloutViewState,
    MeasureDisplayPolicy,
    MeasureNormalDistanceLineRenderInput,
    MeasurePairMeasurementViewState,
} from '../types/measure-callout-types';
import {
    createDefaultMeasureDisplayPolicy,
    MeasureDxDyDzLineRenderMode,
    MeasureLineVisibilityGroup,
} from '../types/measure-callout-types';
import { isMeasurePointObject } from '../selection/MeasureHitResolver';
import type { MeasureEntityRef } from '../types/measure-types';
import { MeasureGeometryKind } from '../types/measure-property-types';
import { resolveUnboundedGeometryKind } from '../property/MeasureUnboundedPropertyResolver';
import type { MeasureReferencePolicy } from '../types/measure-reference-policy-types';
import {
    createDefaultMeasureReferencePolicy,
    MeasurePairReferenceMode,
} from '../types/measure-reference-policy-types';
import { MeasureCalloutRenderer } from './MeasureCalloutRenderer';
import { MeasureDxDyDzLineRenderer } from './MeasureDxDyDzLineRenderer';
import { MeasureNormalDistanceLineRenderer } from './MeasureNormalDistanceLineRenderer';
import { MeasureNormalDistanceResolver } from '../property/MeasureNormalDistanceResolver';
import {
    buildMeasurePointAxisDisplayRows,
    meterCoordinateTupleToMillimeterTuple,
} from '../math/MeasureNumericKernel';

/**
 * 测量标注管理器。
 *
 * 职责：
 * 1. 绑定测量会话生命周期，控制锚点与测量线 overlay；
 * 2. 单选通道：同步属性结果；“最后一次点击”信息锚点仅在点到点模式渲染；
 * 3. 双选通道：求解参考点与距离数值，始终渲染两端最近点锚点；三维虚线由结果项选中/hover 驱动。
 */
export class MeasureCalloutManager {
    /** Viewer 实例。 */
    private readonly viewer: Viewer;

    /** 3D 锚点渲染器。 */
    private readonly renderer: MeasureCalloutRenderer;

    /** ΔX/ΔY/ΔZ 测量线渲染器。 */
    private readonly lineRenderer: MeasureDxDyDzLineRenderer;

    /** 垂直距离测量线渲染器。 */
    private readonly normalDistanceLineRenderer: MeasureNormalDistanceLineRenderer;

    /** 测量属性解析器。 */
    private readonly propertyResolver: MeasurePropertyResolver;

    /** 双选参考点求解门面。 */
    private readonly pairReferenceResolver: MeasurePairReferencePointResolver;

    /** 分量差计算器。 */
    private readonly dxDyDzCalculator: MeasureDxDyDzCalculator;

    /** 垂直距离求解器。 */
    private readonly normalDistanceResolver: MeasureNormalDistanceResolver;

    /** 测量会话是否激活。 */
    private isSessionActive: boolean = false;

    /** 当前浮窗视图状态（单选）。 */
    private viewState: MeasureCalloutViewState | null = null;

    /** 当前双选距离视图状态。 */
    private pairMeasurement: MeasurePairMeasurementViewState | null = null;

    /** 双选结果区是否列出 XYZ 分量。 */
    private displayPolicy: MeasureDisplayPolicy = createDefaultMeasureDisplayPolicy();

    /** 当前双选参考点策略（决定是否渲染拾取锚点）。 */
    private referencePolicy: MeasureReferencePolicy = createDefaultMeasureReferencePolicy();

    /**
     * 当前测量线可视化选中组（点选钉住，驱动 UI 选中态）。
     */
    private lineVisibilityGroup: MeasureLineVisibilityGroup = MeasureLineVisibilityGroup.None;

    /**
     * hover 预览组；非空时覆盖点选组驱动三维虚线，不影响 UI 选中态。
     * null 表示无 hover 预览。
     */
    private hoverPreviewGroup: MeasureLineVisibilityGroup | null = null;

    /** 状态变化事件流。 */
    private readonly change$ = new Subject<MeasureCalloutChangeEvent>();

    /**
     * @param viewer Viewer 实例。
     */
    public constructor(viewer: Viewer) {
        this.viewer = viewer;
        this.renderer = new MeasureCalloutRenderer(viewer);
        this.lineRenderer = new MeasureDxDyDzLineRenderer(viewer, MeasureDxDyDzLineRenderMode.Hidden);
        this.normalDistanceLineRenderer = new MeasureNormalDistanceLineRenderer(viewer);
        this.propertyResolver = new MeasurePropertyResolver(viewer);
        this.pairReferenceResolver = new MeasurePairReferencePointResolver(viewer);
        this.dxDyDzCalculator = new MeasureDxDyDzCalculator();
        this.normalDistanceResolver = new MeasureNormalDistanceResolver(viewer);
    }

    /**
     * 订阅测量 callout 状态变化。
     *
     * @returns 状态变化 Observable。
     */
    public observeCalloutChange(): Observable<MeasureCalloutChangeEvent> {
        return this.change$.asObservable();
    }

    /**
     * 获取当前浮窗视图状态快照。
     *
     * @returns 视图状态副本；无活动锚点时返回 null。
     */
    public getCalloutViewState(): MeasureCalloutViewState | null {
        if (!this.viewState) {
            return null;
        }
        return {
            ...this.viewState,
            worldPickPoint: this.viewState.worldPickPoint.clone(),
            rows: this.viewState.rows.map((row) => ({ ...row })),
        };
    }

    /**
     * 获取当前双选距离视图状态快照。
     *
     * @returns 双选状态副本；无双选测量时返回 null。
     */
    public getPairMeasurementViewState(): MeasurePairMeasurementViewState | null {
        if (!this.pairMeasurement) {
            return null;
        }
        return {
            ...this.pairMeasurement,
            from: this.pairMeasurement.from.clone(),
            to: this.pairMeasurement.to.clone(),
            normalFrom: this.pairMeasurement.normalFrom?.clone(),
            normalTo: this.pairMeasurement.normalTo?.clone(),
            transverseFrom: this.pairMeasurement.transverseFrom?.clone(),
            transverseTo: this.pairMeasurement.transverseTo?.clone(),
            callouts: this.pairMeasurement.callouts.map((callout) => ({
                ...callout,
                worldAnchor: callout.worldAnchor.clone(),
            })),
        };
    }

    /**
     * 获取当前测量线可视化选中组（点选钉住态，不含 hover 预览）。
     *
     * @returns 选中组。
     */
    public getLineVisibilityGroup(): MeasureLineVisibilityGroup {
        return this.lineVisibilityGroup;
    }

    /**
     * 设置测量线可视化选中组并刷新三维虚线。
     *
     * 设计说明：再点同一组视为取消选中（置为 None）；不影响当前 hover 预览。
     *
     * @param group 目标选中组。
     * @param options.toggleSame 为 true 时，若与当前组相同则切到 None。
     */
    public setLineVisibilityGroup(
        group: MeasureLineVisibilityGroup,
        options: { toggleSame?: boolean } = {},
    ): void {
        const nextGroup = options.toggleSame && group === this.lineVisibilityGroup
            ? MeasureLineVisibilityGroup.None
            : group;
        if (nextGroup === this.lineVisibilityGroup && this.isSessionActive) {
            // 同组重复设置时仍刷新一次线，保证与缓存几何一致
            this.applyLineVisibility();
            this.emitChange();
            return;
        }
        this.lineVisibilityGroup = nextGroup;
        this.applyLineVisibility();
        this.emitChange();
    }

    /**
     * 设置结果项 hover 预览组（仅影响三维虚线，不改变点选钉住态）。
     *
     * @param group 预览组；传 null 或 {@link MeasureLineVisibilityGroup.None} 清除预览。
     */
    public setLineVisibilityHoverPreview(group: MeasureLineVisibilityGroup | null): void {
        if (!this.isSessionActive) {
            return;
        }
        const nextPreview = !group || group === MeasureLineVisibilityGroup.None
            ? null
            : group;
        if (nextPreview === this.hoverPreviewGroup) {
            this.applyLineVisibility();
            return;
        }
        this.hoverPreviewGroup = nextPreview;
        this.applyLineVisibility();
    }

    /**
     * 测量会话启动时调用。
     *
     * @param policy 双选参考点策略。
     * @param pickHistory 点击历史。
     */
    public onSessionStart(
        policy: MeasureReferencePolicy,
        pickHistory: readonly MeasureEntityRef[] = [],
    ): void {
        this.isSessionActive = true;
        this.displayPolicy = createDefaultMeasureDisplayPolicy();
        this.referencePolicy = {
            pairMode: policy.pairMode,
            arcCircleMode: policy.arcCircleMode,
        };
        this.lineVisibilityGroup = MeasureLineVisibilityGroup.None;
        this.hoverPreviewGroup = null;
        this.publishMeasurementState(null, null, policy, pickHistory);
    }

    /**
     * 测量会话结束时调用。
     */
    public onSessionStop(): void {
        this.isSessionActive = false;
        this.displayPolicy = createDefaultMeasureDisplayPolicy();
        this.referencePolicy = createDefaultMeasureReferencePolicy();
        this.lineVisibilityGroup = MeasureLineVisibilityGroup.None;
        this.hoverPreviewGroup = null;
        this.renderer.clearAnchors();
        this.lineRenderer.clear();
        this.normalDistanceLineRenderer.clear();
        this.viewState = null;
        this.pairMeasurement = null;
        this.emitChange();
    }

    /**
     * 应用双选结果显示策略并刷新结果条目（不自动画线）。
     *
     * @param policy 显示策略快照。
     */
    public applyDisplayPolicy(policy: MeasureDisplayPolicy): void {
        this.displayPolicy = {
            showDxDyDzMeasurement: policy.showDxDyDzMeasurement,
        };
        // 关闭 XYZ 列表时，清除已钉住/预览的分量选中
        if (!this.displayPolicy.showDxDyDzMeasurement) {
            if (isMeasureAxisVisibilityGroup(this.lineVisibilityGroup)) {
                this.lineVisibilityGroup = MeasureLineVisibilityGroup.None;
            }
            if (this.hoverPreviewGroup && isMeasureAxisVisibilityGroup(this.hoverPreviewGroup)) {
                this.hoverPreviewGroup = null;
            }
        }
        this.refreshPairMeasurementPresentation();
    }

    /**
     * 同步当前活动锚点（最近一次点击几何的信息锚点）。
     *
     * 设计说明：该锚点原用于视口单选浮窗/引线，现仅在点到点模式下渲染以标识拾取点；
     * 双选最近点锚点不受此限制，由 {@link syncPairMeasurement} 始终维护。
     *
     * @param entity 活动锚点实体；无活动对象时传 null。
     * @param policy 可选参考点策略；传入时同步缓存并据此决定是否渲染信息锚点。
     */
    public syncActiveAnchor(
        entity: MeasureEntityRef | null,
        policy?: MeasureReferencePolicy,
    ): void {
        if (!this.isSessionActive) {
            return;
        }
        if (policy) {
            this.referencePolicy = {
                pairMode: policy.pairMode,
                arcCircleMode: policy.arcCircleMode,
            };
        }
        const shouldRenderPickAnchor = this.shouldRenderPickAnchors();
        const anchorEntity = shouldRenderPickAnchor
            && entity
            && !this.shouldSuppressSingleSelectAnchor(entity)
            ? entity
            : null;
        this.renderer.setActiveAnchor(anchorEntity);
        this.viewState = entity ? this.propertyResolver.resolve(entity) : null;
        this.emitChange();
    }

    /**
     * 同步设计树节点原点结果。
     *
     * @param input 节点原点展示输入。
     */
    public syncTreeNodeOrigin(input: {
        objectId: string;
        displayName: string;
        entityKindLabel: string;
        originWorld: THREE.Vector3;
    }): void {
        if (!this.isSessionActive) {
            return;
        }

        this.clearPairVisualization();
        // 设计树原点不是点到点拾取点，不渲染锚点
        this.renderer.setActiveAnchor(null);
        this.viewState = {
            objectId: input.objectId,
            entityName: input.displayName,
            entityKindLabel: input.entityKindLabel,
            worldPickPoint: input.originWorld.clone(),
            rows: buildMeasurePointAxisDisplayRows(meterCoordinateTupleToMillimeterTuple([
                input.originWorld.x,
                input.originWorld.y,
                input.originWorld.z,
            ])),
        };
        this.emitChange();
    }

    /**
     * 同步双选实体距离测量。
     *
     * @param firstEntity 先选实体。
     * @param secondEntity 后选实体。
     * @param policy 双选参考点策略。
     * @param pickHistory 测量点击历史。
     */
    public syncPairMeasurement(
        firstEntity: MeasureEntityRef,
        secondEntity: MeasureEntityRef,
        policy: MeasureReferencePolicy,
        pickHistory: readonly MeasureEntityRef[],
    ): void {
        if (!this.isSessionActive) {
            return;
        }

        this.referencePolicy = {
            pairMode: policy.pairMode,
            arcCircleMode: policy.arcCircleMode,
        };

        const closest = this.pairReferenceResolver.resolve(
            firstEntity,
            secondEntity,
            policy,
            pickHistory,
        );
        if (!closest) {
            this.clearPairVisualization();
            this.emitChange();
            return;
        }

        if (!closest.shouldRenderMeasureLines) {
            // 零距离：不发布双选结果，不保留测量线选中态
            this.clearPairVisualization();
            this.emitChange();
            return;
        }

        const deltas = this.dxDyDzCalculator.calculate(closest.from, closest.to);
        const normalDistance = this.normalDistanceResolver.resolve(
            firstEntity,
            secondEntity,
            closest,
        );
        const dxMeters = closest.to.x - closest.from.x;
        const dyMeters = closest.to.y - closest.from.y;
        const dzMeters = closest.to.z - closest.from.z;
        this.pairMeasurement = {
            from: closest.from.clone(),
            to: closest.to.clone(),
            distance: deltas.distance,
            dx: deltas.dx,
            dy: deltas.dy,
            dz: deltas.dz,
            normalDistance: normalDistance?.distance,
            normalFrom: normalDistance?.from.clone(),
            normalTo: normalDistance?.to.clone(),
            transverseFrom: normalDistance?.shouldRenderTransverse
                ? normalDistance.transverseFrom.clone()
                : undefined,
            transverseTo: normalDistance?.shouldRenderTransverse
                ? normalDistance.transverseTo.clone()
                : undefined,
            callouts: buildPairMeasurementCallouts({
                from: closest.from,
                to: closest.to,
                dxMeters,
                dyMeters,
                dzMeters,
                dxMillimeter: deltas.dx,
                dyMillimeter: deltas.dy,
                dzMillimeter: deltas.dz,
                distanceMillimeter: deltas.distance,
                showDxDyDz: this.displayPolicy.showDxDyDzMeasurement,
                normalDistanceMillimeter: normalDistance?.distance,
                normalFrom: normalDistance?.from,
                normalTo: normalDistance?.to,
            }),
        };

        // 双选最近点锚点始终渲染；单选“最后一次点击”信息锚点仅点到点模式显示
        this.renderer.setActiveAnchor(null);
        this.renderer.setPairAnchors(closest.from, closest.to);
        // 新双选结果：保持当前选中组，按组刷新虚线（默认 None 则不画）
        this.applyLineVisibility();
        // 子渲染器与管理器可能连续标脏，统一帧调度器会在同一宿主帧内合并为一次绘制。
        this.viewer.requestRender?.();
        this.emitChange();
    }

    /**
     * 清除双选测量可视化与状态。
     */
    public clearPairMeasurement(): void {
        if (!this.isSessionActive) {
            return;
        }
        this.clearPairVisualization();
        this.emitChange();
    }

    /**
     * 释放管理器持有的全部资源。
     */
    public dispose(): void {
        this.isSessionActive = false;
        this.viewState = null;
        this.pairMeasurement = null;
        this.lineVisibilityGroup = MeasureLineVisibilityGroup.None;
        this.hoverPreviewGroup = null;
        this.renderer.dispose();
        this.lineRenderer.dispose();
        this.normalDistanceLineRenderer.dispose();
        this.change$.complete();
    }

    /**
     * 判断是否应渲染单选信息锚点（最后一次点击几何）。
     *
     * @returns 点到点模式为 true。
     */
    private shouldRenderPickAnchors(): boolean {
        return this.referencePolicy.pairMode === MeasurePairReferenceMode.PointToPoint;
    }

    /**
     * 判断单选锚点是否应对当前实体抑制渲染。
     *
     * @param entity 活动实体引用。
     * @returns 是否抑制单选锚点。
     */
    private shouldSuppressSingleSelectAnchor(entity: MeasureEntityRef): boolean {
        if (entity.unboundedBinding) {
            return resolveUnboundedGeometryKind(entity.unboundedBinding) === MeasureGeometryKind.ReferencePoint;
        }

        const sceneObject = this.viewer.findSceneObjectById(entity.objectId);
        return isMeasurePointObject(sceneObject);
    }

    /**
     * 清除双选测量线、锚点与选中组。
     */
    private clearPairVisualization(): void {
        this.pairMeasurement = null;
        this.lineVisibilityGroup = MeasureLineVisibilityGroup.None;
        this.hoverPreviewGroup = null;
        this.renderer.setPairAnchors(null, null);
        this.lineRenderer.clear();
        this.normalDistanceLineRenderer.clear();
        this.viewer.requestRender?.();
    }

    /**
     * 发布单选与双选状态（会话启动时用）。
     *
     * @param singleEntity 单选实体。
     * @param pair 双选实体对。
     * @param policy 参考点策略。
     * @param pickHistory 点击历史。
     */
    private publishMeasurementState(
        singleEntity: MeasureEntityRef | null,
        pair: readonly [MeasureEntityRef, MeasureEntityRef] | null,
        policy: MeasureReferencePolicy,
        pickHistory: readonly MeasureEntityRef[],
    ): void {
        if (pair) {
            this.syncPairMeasurement(pair[0], pair[1], policy, pickHistory);
            return;
        }
        this.clearPairVisualization();
        this.syncActiveAnchor(singleEntity, policy);
    }

    /**
     * 按当前有效可见性组刷新三维虚线（hover 预览优先于点选钉住）。
     */
    private applyLineVisibility(): void {
        if (!this.pairMeasurement) {
            this.lineRenderer.clear();
            this.normalDistanceLineRenderer.clear();
            return;
        }

        const effectiveGroup = this.hoverPreviewGroup ?? this.lineVisibilityGroup;
        const {
            from,
            to,
            distance,
            normalFrom,
            normalTo,
            transverseFrom,
            transverseTo,
        } = this.pairMeasurement;
        const dxMeters = to.x - from.x;
        const dyMeters = to.y - from.y;
        const dzMeters = to.z - from.z;
        const dxDyDzInput = {
            from,
            to,
            dx: dxMeters,
            dy: dyMeters,
            dz: dzMeters,
            distance,
        };
        const normalInput = buildNormalDistanceLineRenderInputFromState(
            normalFrom,
            normalTo,
            transverseFrom,
            transverseTo,
        );

        switch (effectiveGroup) {
            case MeasureLineVisibilityGroup.Distance:
                this.lineRenderer.setRenderMode(MeasureDxDyDzLineRenderMode.DistanceOnly);
                this.lineRenderer.setMeasureLines(dxDyDzInput);
                this.normalDistanceLineRenderer.clear();
                break;
            case MeasureLineVisibilityGroup.Dx:
            case MeasureLineVisibilityGroup.Dy:
            case MeasureLineVisibilityGroup.Dz:
                // UI 互斥单选，三维仍同时展示三条 XYZ 分量虚线
                this.lineRenderer.setRenderMode(MeasureDxDyDzLineRenderMode.XyzOnly);
                this.lineRenderer.setMeasureLines(dxDyDzInput);
                this.normalDistanceLineRenderer.clear();
                break;
            case MeasureLineVisibilityGroup.NormalDistance:
                this.lineRenderer.clear();
                this.normalDistanceLineRenderer.setNormalDistanceLine(normalInput);
                break;
            case MeasureLineVisibilityGroup.None:
            default:
                this.lineRenderer.clear();
                this.normalDistanceLineRenderer.clear();
                break;
        }
    }

    /**
     * 基于已缓存的双选状态刷新结果条目，并按选中组重绘画线。
     */
    private refreshPairMeasurementPresentation(): void {
        if (!this.pairMeasurement) {
            this.applyLineVisibility();
            this.emitChange();
            return;
        }

        const {
            from,
            to,
            distance,
            dx,
            dy,
            dz,
            normalDistance,
            normalFrom,
            normalTo,
            transverseFrom,
            transverseTo,
        } = this.pairMeasurement;
        const dxMeters = to.x - from.x;
        const dyMeters = to.y - from.y;
        const dzMeters = to.z - from.z;
        this.pairMeasurement = {
            from: from.clone(),
            to: to.clone(),
            distance,
            dx,
            dy,
            dz,
            normalDistance,
            normalFrom: normalFrom?.clone(),
            normalTo: normalTo?.clone(),
            transverseFrom: transverseFrom?.clone(),
            transverseTo: transverseTo?.clone(),
            callouts: buildPairMeasurementCallouts({
                from,
                to,
                dxMeters,
                dyMeters,
                dzMeters,
                dxMillimeter: dx,
                dyMillimeter: dy,
                dzMillimeter: dz,
                distanceMillimeter: distance,
                showDxDyDz: this.displayPolicy.showDxDyDzMeasurement,
                normalDistanceMillimeter: normalDistance,
                normalFrom,
                normalTo,
            }),
        };

        this.applyLineVisibility();
        this.viewer.requestRender?.();
        this.emitChange();
    }

    /**
     * 派发状态变化事件。
     */
    private emitChange(): void {
        this.change$.next({
            viewState: this.getCalloutViewState(),
            pairMeasurement: this.getPairMeasurementViewState(),
            lineVisibilityGroup: this.lineVisibilityGroup,
        });
    }
}

/**
 * 由已缓存的双选状态构建垂直距离渲染输入。
 *
 * @param normalFrom 垂直距离线起点。
 * @param normalTo 垂直距离线终点。
 * @param transverseFrom 正交辅助线起点。
 * @param transverseTo 正交辅助线终点。
 * @returns 渲染输入。
 */
function buildNormalDistanceLineRenderInputFromState(
    normalFrom?: THREE.Vector3,
    normalTo?: THREE.Vector3,
    transverseFrom?: THREE.Vector3,
    transverseTo?: THREE.Vector3,
): MeasureNormalDistanceLineRenderInput | null {
    if (!normalFrom || !normalTo) {
        return null;
    }

    return {
        from: normalFrom,
        to: normalTo,
        transverseFrom,
        transverseTo,
    };
}

/**
 * 判断可见性组是否为 ΔX / ΔY / ΔZ 分量选中。
 *
 * @param group 可见性组。
 * @returns 是否为轴分量组。
 */
function isMeasureAxisVisibilityGroup(group: MeasureLineVisibilityGroup): boolean {
    return group === MeasureLineVisibilityGroup.Dx
        || group === MeasureLineVisibilityGroup.Dy
        || group === MeasureLineVisibilityGroup.Dz;
}
